import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { AnserTip } from "@/components/coach/anser-tip";
import type { CelebrateItem, DeadlineCard, TalkRow } from "@/components/overview/overview-model";

/**
 * The home page's "Idag" panel (the platform sketch): what is due, who to
 * talk to and something worth celebrating, shown in the panel instead of
 * the tips until a chat thread starts. Plain data, so the override can be
 * compared by content.
 */
export interface TodayPanelData {
  /** "Torsdag 29 oktober · Vecka 44". */
  dateLabel: string;
  deadlines: DeadlineCard[];
  celebrate: CelebrateItem[];
  /** Booked 1:1s plus overdue rows (GRA-203), soonest/most urgent first. */
  talks: TalkRow[];
}

/**
 * What a page tells the Anser panel about itself (GRA-149). Everything is
 * optional: a page that sets only `label` keeps the route's default intro and
 * the data-driven tips; a page with its own tips (Översikt, a member profile)
 * replaces them.
 */
export interface AnserPanelOverride {
  /** The context line under "Anser" in the panel header, e.g. a member's name. */
  label?: string;
  /** `intelligence:anser.intro.<key>`; falls back to the route default. */
  introKey?: string;
  /** Tips shown on an empty thread instead of the data-driven ones. */
  tips?: AnserTip[];
  /** The two quick-question chips under the tips. */
  quickQuestions?: string[];
  /** The home page's Idag panel, replacing the tips on an empty thread. */
  today?: TodayPanelData;
}

interface AnserPanelContextValue {
  override: AnserPanelOverride | null;
  setOverride: (next: AnserPanelOverride | null) => void;
}

const AnserPanelContext = createContext<AnserPanelContextValue | undefined>(
  undefined,
);

export function AnserPanelProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<AnserPanelOverride | null>(null);
  const value = useMemo(() => ({ override, setOverride }), [override]);
  return (
    <AnserPanelContext.Provider value={value}>
      {children}
    </AnserPanelContext.Provider>
  );
}

/**
 * The current page's override, or null outside the provider (tests render
 * the panel bare) and when no page has set one.
 */
export function useAnserPanelOverride(): AnserPanelOverride | null {
  return useContext(AnserPanelContext)?.override ?? null;
}

/**
 * Pages call this to describe themselves to the panel. The override is set
 * in an effect and cleared on unmount, so navigating away always falls back
 * to the route default. Pass the pieces you have; `undefined` fields are
 * ignored. Stable inputs keep the effect quiet (memoise arrays).
 */
export function useAnserPanelContext(override: AnserPanelOverride | null) {
  const ctx = useContext(AnserPanelContext);
  const setOverride = ctx?.setOverride;
  // Pages rebuild their tips and chips on most renders, and setting the
  // override re-renders the page (it reads this context). Comparing by
  // content, not identity, is what keeps that from becoming a render loop.
  // The override is plain data, so its JSON form carries everything.
  const fingerprint = override ? JSON.stringify(override) : null;
  useEffect(() => {
    if (!setOverride) return;
    if (fingerprint === null) {
      setOverride(null);
      return;
    }
    setOverride(JSON.parse(fingerprint) as AnserPanelOverride);
    return () => setOverride(null);
  }, [setOverride, fingerprint]);
}
